import { DateTime } from 'luxon';

export class DateHelper {
  /**
   * Normalizes an arbitrary date-like value to an ISO date
   * string (YYYY-MM-DD). Accepts ISO/SQL strings, JS Dates and
   * Luxon DateTimes. Returns null for empty or unparseable input.
   */
  public static toIsoDate(value: string | Date | DateTime | null | undefined): string | null {
    if (!value) {
      return null;
    }

    let dt: DateTime;
    if (value instanceof DateTime) {
      dt = value;
    } else if (value instanceof Date) {
      dt = DateTime.fromJSDate(value);
    } else {
      dt = DateTime.fromISO(value);
      if (!dt.isValid) {
        dt = DateTime.fromSQL(value);
      }
    }

    return dt.isValid ? dt.toISODate() : null;
  }

  /**
   * Checks whether a date of birth (month & day)
   * is within a given date range.
   * The year is intentionally ignored.
   */
  public static isDateOfBirthBetweenDates(
    date: DateTime | string | Date,
    startDate: DateTime = DateTime.local().minus({ days: 3 }),
    endDate: DateTime = DateTime.local().plus({ days: 3 })
  ): boolean {
    if (!date) {
      return false;
    }

    const toMonthDay = (dt: DateTime) => ({
      month: dt.month,
      day: dt.day
    });

    let target: DateTime;

    if (date instanceof DateTime) {
      target = date;
    } else if (date instanceof Date) {
      target = DateTime.fromJSDate(date);
    } else {
      target = DateTime.fromISO(date);
    }

    if (!target.isValid) {
      return false;
    }

    const targetMD = toMonthDay(target);
    const startMD = toMonthDay(startDate);
    const endMD = toMonthDay(endDate);

    // Case 1: Range does not cross the year boundary
    if (
      startMD.month < endMD.month ||
      (startMD.month === endMD.month && startMD.day <= endMD.day)
    ) {
      return (
        (targetMD.month > startMD.month ||
          (targetMD.month === startMD.month && targetMD.day >= startMD.day)) &&
        (targetMD.month < endMD.month ||
          (targetMD.month === endMD.month && targetMD.day <= endMD.day))
      );
    }

    // Case 2: Range crosses the year boundary (e.g. Dec → Jan)
    return (
      targetMD.month > startMD.month ||
      (targetMD.month === startMD.month && targetMD.day >= startMD.day) ||
      targetMD.month < endMD.month ||
      (targetMD.month === endMD.month && targetMD.day <= endMD.day)
    );
  }

  /**
   * Checks whether today is the person's birthday.
   * The year is intentionally ignored.
   */
  public static isBirthdayToday(
    date: DateTime | string | Date
  ): boolean {
    if (!date) {
      return false;
    }

    let birth: DateTime;

    if (date instanceof DateTime) {
      birth = date;
    } else if (date instanceof Date) {
      birth = DateTime.fromJSDate(date);
    } else {
      birth = DateTime.fromISO(date);
    }

    if (!birth.isValid) {
      return false;
    }

    const today = DateTime.local();

    return (
      birth.month === today.month &&
      birth.day === today.day
    );
  }
}